import { useTheme } from "@emotion/react";
import Link from "next/link";
import { useCssMediaQuery } from "src/components/base/hooks/useCssMediaQuery";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { global_config } from "src/shared/global_config";
import { routes } from "src/shared/routes";
import { TTeamConfig } from "src/shared/types/teamConfig.types";
import { openPdfInNewTab } from "src/utils/utils";
import { constants } from "src/shared/constants";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";
import Head from "next/head";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    group: "Games/Months Included",
    question: "What games are included with each monthly pass?",
    answer:
      "Each month’s payment gives you access to the included games that month. You can review the monthly schedule on your homepage. Games against the following opponents are excluded from this season’s Kings Pass: Celtics, Lakers, Pistons and Warriors.",
  },

  {
    group: "Games/Months Included",
    question: "Does my pass automatically renew each month?",
    answer:
      "The monthly Kings Pass will automatically renew for the next month through March 2025. There is an opt out window each month if you do not want your pass to renew. If you purchase your pass at the season price, you will be automatically charged each month through March. There is no opt out window with the season pass.",
  },
  {
    group: "Games/Months Included",
    question:
      "How do I opt out of next month if I don’t want to continue my Kings Pass (monthly pass only)?",
    answer: (
      <>
        You must opt out by the 25th of the prior month if you want to
        discontinue your Kings Pass. For example, if you want to discontinue
        your pass for November, you need to opt out by October 25th. To opt out,
        log in to your Kings Pass account and select “
        <Link
          href={{
            pathname: routes.manage_subscription,
            query: { teamSchema: "sacramentokings_pass" },
          }}
        >
          Manage Subscription
        </Link>
        .”
      </>
    ),
  },
  {
    group: "Pass/Seat Locations",
    question: "Where can I expect to sit each game?",
    answer:
      "You are guaranteed a seat in the upper level, but your specific seat location will likely vary game to game. Please note that seats will be randomly assigned to pass buyers for each game, so specific seat locations cannot be guaranteed.",
  },
  {
    group: "Pass/Seat Locations",
    question: "How do I get my seat location(s) and ticket(s)?",
    answer:
      "You will receive a text message the day before each game asking if you will be attending the next game. You will need to log in to your Kings Pass account to check in for each game. If you indicate that you’re attending that game, you will receive a text message a few hours before the game with a link to your tickets with your seat location.",
  },

  {
    group: "Pass/Seat Locations",
    question: "What if I lose the text message with my pass?",
    answer: (
      <>
        You can access your tickets by going to{" "}
        <Link href={"https://www.kings.com/ClickTix"} target="_blank">
          Kings.com/ClickTix
        </Link>{" "}
        and logging into your account.
      </>
    ),
  },
  {
    group: "Pass/Seat Locations",
    question:
      "If I buy two or more passes, will my seats be next to each other? ",
    answer:
      "Yes, passes purchased together will receive their seats next to each other.",
  },
  {
    group: "Pass/Seat Locations",
    question:
      "Can my seat be next to a friend who also purchased the Kings Pass?",
    answer: (
      <>
        Yes, those who purchase the Kings Pass will have the opportunity to link
        their pass to another pass buyer in order to sit next to each other. You
        can do this by logging into your Kings Pass account and clicking “
        <Link
          href={{
            pathname: routes.my_group,
            query: { teamSchema: "sacramentokings_pass" },
          }}
        >
          My Group
        </Link>
        ” in the menu. Generate a unique link to share with friends to sit with
        you this season and to manage your existing group. You will need to
        ensure your group is created and all members have joined at least 4
        hours prior to the game in order to be seated together.
      </>
    ),
  },

  {
    group: "Other",
    question: "How many passes can I buy?",
    answer: "You can buy up to four passes per account.",
  },
  {
    group: "Other",
    question: "Can I get a refund for unused tickets?",
    answer: "Passes are non-transferable and non-refundable.",
  },
  {
    group: "Other",
    question: "Can I sell my pass for games that I do not attend?",
    answer: "No, passes are non-transferable.",
  },

  {
    group: "Other",
    question: "Can I share my pass with someone else?",
    answer: "Passes cannot be shared and are non-transferable.",
  },
  {
    group: "Other",
    question: "What do I need to access my digital tickets for the game? ",
    answer:
      "You will need a smartphone with a web browser to access and display your digital tickets for admission to Golden 1 Center.",
  },
  {
    group: "Other",
    question: "How can I change my payment method for my Kings Pass?",
    answer: (
      <>
        Navigate to the{" "}
        <Link
          href={{
            pathname: routes.manage_subscription,
            query: { teamSchema: "sacramentokings_pass" },
          }}
        >
          Manage Subscription
        </Link>{" "}
        menu on the right side of your screen. You will see a section to update
        your payment method where you can input your new information.
      </>
    ),
  },
];

export const TERMS_AND_CONDITION = () => {
  const theme = useSSTheme();
  const { md: mdUp } = useCssMediaQuery("up");
  const router = useRouterExt();
  return (
    <>
      By purchasing you understand that you are agreeing to a monthly
      subscription. It will renew each month for $119 unless you cancel during
      the defined opt-out period. Tickets are non-transferable and not eligible
      for resale. You also agree to receive SMS alerts regarding ticket delivery
      and agree to the{" "}
      <span
        onClick={(ev) => {
          ev?.preventDefault();
          ev.stopPropagation();
          openPdfInNewTab(global_config.TERMS_AND_CONDITIONS_PDF_URL);
        }}
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Terms and Conditions
      </span>{" "}
      and{" "}
      <span
        onClick={(ev) => {
          ev?.preventDefault();
          ev.stopPropagation();
          openPdfInNewTab(global_config.PRIVACY_POLICY_PDF_URL);
        }}
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Privacy Policy
      </span>
      .
    </>
  );
};

export const TERMS_AND_CONDITION_OPT_OUT_RESTRICTED = () => {
  const theme = useSSTheme();
  return (
    <>
      By purchasing you understand that you are agreeing to a monthly
      subscription with no opt-out. The subscription will be billed at
      $109/month from November through March. Tickets are non-transferable and
      not eligible for resale. You also agree to receive SMS alerts regarding
      ticket delivery and agree to the{" "}
      <span
        onClick={(ev) => {
          ev?.preventDefault();
          ev.stopPropagation();
          openPdfInNewTab(global_config.TERMS_AND_CONDITIONS_PDF_URL);
        }}
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Terms and Conditions
      </span>{" "}
      and{" "}
      <span
        onClick={(ev) => {
          ev?.preventDefault();
          ev.stopPropagation();
          openPdfInNewTab(global_config.PRIVACY_POLICY_PDF_URL);
        }}
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Privacy Policy
      </span>
      .
    </>
  );
};

export const TERMS_AND_CONDITION_ADD_ON = () => {
  const theme = useSSTheme();
  return (
    <>
      Tickets are non-transferable and not eligible for resale. You also agree
      to receive SMS alerts regarding ticket delivery and agree to the{" "}
      <span
        onClick={(ev) => {
          ev?.preventDefault();
          ev.stopPropagation();
          openPdfInNewTab(global_config.TERMS_AND_CONDITIONS_PDF_URL);
        }}
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Terms and Conditions
      </span>{" "}
      and{" "}
      <span
        onClick={(ev) => {
          ev?.preventDefault();
          ev.stopPropagation();
          openPdfInNewTab(global_config.PRIVACY_POLICY_PDF_URL);
        }}
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Privacy Policy
      </span>
      .
    </>
  );
};

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <Head>
      <script
        defer
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        id={`${TEAM_SCHEMA}-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '1846888515544840');
              fbq('track', 'PageView');
              window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
                const { cartRes } = e.detail;
                fbq('track', 'Purchase', {
                  value: cartRes.totalAmount,
                  currency: 'USD',
                });
                console.log('meta pixel purchase event fired.');
              });
            `,
        }}
      />
    </Head>
  );
};
